import type { ReactNode } from 'react'

import { Field, Input } from '@/components/field'
import type { SlugVerdict } from '@/features/restaurants/use-slug-verdict'
import { ApiError } from '@/lib/api'
import { useT } from '@/lib/i18n/translate'
import { SLUG_MAX_LEN } from '@/lib/slug'

export interface SlugFieldProps {
  label: ReactNode
  value: string
  onChange: (value: string) => void
  verdict: SlugVerdict
  /** After a submit attempt, an empty value shows its problem too. */
  submitted?: boolean
  /** The server's verdict on this exact value (SLUG_TAKEN / SLUG_RESERVED). */
  error?: unknown
  hint?: ReactNode
}

export function SlugField({ label, value, onChange, verdict, submitted = false, error, hint }: SlugFieldProps) {
  const t = useT()
  const serverError = error instanceof ApiError && error.code.startsWith('SLUG_') ? error : undefined
  const showProblem = verdict.problem && (value !== '' || submitted)
  const fieldError =
    serverError ??
    (showProblem && verdict.problem
      ? t(verdict.problem.key, verdict.problem.params)
      : verdict.availability === 'taken'
        ? t('error.SLUG_TAKEN')
        : verdict.availability === 'reserved'
          ? t('error.SLUG_RESERVED')
          : undefined)
  const status =
    verdict.availability === 'checking' ? (
      <span>{t('slug.checking')}</span>
    ) : verdict.availability === 'available' ? (
      <span role="status" className="text-success">
        {t('slug.available')}
      </span>
    ) : (
      hint
    )

  return (
    <Field label={label} error={fieldError} hint={status}>
      <Input
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        maxLength={SLUG_MAX_LEN}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  )
}
